import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../lib/api'

export interface Warehouse {
  id: string
  tenant_id: string
  code_1c: string
  name: string
  is_active: boolean
  created_at: string
  updated_at?: string
}

export interface WarehouseFilters {
  search?: string
  is_active?: boolean
  page?: number
  limit?: number
  sort?: string
}

export interface PaginatedWarehouses {
  data: Warehouse[]
  total: number
  page: number
  limit: number
  totalPages: number
}

const WAREHOUSES_QUERY_KEY = 'warehouses'

export const useWarehouses = (filters: WarehouseFilters = {}) => {
  return useQuery({
    queryKey: [WAREHOUSES_QUERY_KEY, filters],
    queryFn: async (): Promise<PaginatedWarehouses> => {
      const response = await apiClient.getWarehouses(filters)
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
        totalPages: response.totalPages || 1,
      }
    },
    staleTime: 30 * 1000, // 30 seconds
    retry: 3,
  })
}

export const useWarehouse = (id: string) => {
  return useQuery({
    queryKey: [WAREHOUSES_QUERY_KEY, id],
    queryFn: async (): Promise<Warehouse> => {
      const response = await apiClient.getWarehouse(id)
      return response.data
    },
    enabled: !!id,
    staleTime: 30 * 1000,
  })
}

// Hook for select dropdowns - returns simplified list of all warehouses
export const useWarehousesForSelect = () => {
  return useQuery({
    queryKey: [WAREHOUSES_QUERY_KEY, 'for-select'],
    queryFn: async (): Promise<Array<{ id: string; name: string; code_1c: string }>> => {
      const response = await apiClient.getWarehouses({ limit: 1000, is_active: true })
      return (response.data || []).map((w: Warehouse) => ({
        id: w.id,
        name: w.name,
        code_1c: w.code_1c,
      }))
    },
    staleTime: 60 * 1000, // 1 minute
  })
}
